/**
 * Addons Service — frontend client for the credits-billed addons.
 *
 * Two addons currently in scope; each follows the same lifecycle:
 *
 *   GET    /addons/<addon>/status     → current state, rate, EIP/quantity
 *   POST   /addons/<addon>/enable     → provision + start meter (body opt)
 *   DELETE /addons/<addon>/disable    → tear down + stop meter
 *
 * After Wave B-2 lands, /billing/info embeds the per-addon shape under
 * `addons.dedicatedEgress` and `addons.warmPool`, so the BillingPage can
 * render the cards from a single response without N+1 fetches. The
 * per-addon endpoints below are kept for ENABLE/DISABLE actions (they
 * touch infra + ledger, so the dedicated handlers stay the source of
 * truth for mutation).
 */
import { accountApi as apiService } from './api';

const Addons = {
  // ── Dedicated Egress ──────────────────────────────────────────────
  async enableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/enable', { method: 'POST' });
  },
  async disableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/disable', { method: 'DELETE' });
  },
  async getDedicatedEgressStatus() {
    return apiService.request('/addons/dedicated-egress/status');
  },

  // ── Warm Pool ─────────────────────────────────────────────────────
  /**
   * Enable warm pool with `quantity` runners.
   *
   *   - Account-level (BillingPage):         enableWarmPool(q)
   *   - Per-pipeline (Settings page):        enableWarmPool(q, { projectId, workflowType })
   *
   * Per-pipeline mode atomically flips BOTH workflow.warmEnabled and
   * account.warmPool — that's what makes the reconciler actually
   * provision the pool. Don't omit the workflow identifiers from the
   * settings flow or you'll get a charged-but-empty pool.
   */
  async enableWarmPool(quantity, opts = {}) {
    const q = Number(quantity);
    if (!Number.isInteger(q) || q < 1) {
      throw new Error('quantity must be a positive integer');
    }
    const body = { quantity: q };
    if (opts?.projectId) body.projectId = opts.projectId;
    if (opts?.workflowType) body.workflowType = opts.workflowType;
    return apiService.request('/addons/warm-pool/enable', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * Disable warm pool.
   *
   *   - Account-level:     disableWarmPool()
   *   - Per-pipeline:      disableWarmPool({ projectId, workflowType })
   *
   * Per-pipeline disables JUST that workflow's flag and resizes the
   * account pool to the MAX warmCount of remaining warm workflows
   * (or fully tears down billing if no warm workflows are left).
   */
  async disableWarmPool(opts = {}) {
    let path = '/addons/warm-pool/disable';
    if (opts?.projectId && opts?.workflowType) {
      const qs = new URLSearchParams({
        projectId: opts.projectId,
        workflowType: opts.workflowType,
      });
      path += `?${qs.toString()}`;
    }
    return apiService.request(path, { method: 'DELETE' });
  },
  /**
   * Warm-pool status. Account-level when called bare (BillingPage). Pass
   * { projectId, workflowType } for the PER-PIPELINE status (pipeline
   * Settings) — the backend then reports this pipeline's own warm state
   * (from the workflow row), not the caller account's pool.
   */
  async getWarmPoolStatus(opts = {}) {
    let path = '/addons/warm-pool/status';
    if (opts.projectId && opts.workflowType) {
      const qs = new URLSearchParams({
        projectId: opts.projectId,
        workflowType: opts.workflowType,
      });
      path += `?${qs.toString()}`;
    }
    return apiService.request(path);
  },
};

export default Addons;
